<template>
  <v-container fluid>
    <v-card-title class="text-h5 font-weight-bold mb-4"> Einsatzplan-Übersicht für {{ monthName }} </v-card-title>

    <div v-if="loading" class="text-center">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4">Plan wird geladen...</p>
    </div>

    <v-card v-else outlined class="rounded-lg">
      <v-row no-gutters class="roster-header pa-2 d-none d-md-flex">
        <v-col md="3" class="font-weight-bold">Mitarbeiter</v-col>
        <v-col md="9">
          <v-row no-gutters>
            <v-col
              v-for="day in daysInMonth"
              :key="`header-${day.format('YYYY-MM-DD')}`"
              class="text-center day-cell-header"
              :class="{ weekend: day.isoWeekday() >= 6 }">
              <div class="text-caption">{{ day.format("dd") }}</div>
              <div>{{ day.format("D") }}</div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-expansion-panels accordion flat hover>
        <v-expansion-panel v-for="employee in processedEmployees" :key="employee._id" class="employee-panel">
          <v-expansion-panel-header class="employee-header">
            <v-row no-gutters align="center">
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-avatar size="40" class="mr-3">
                  <v-img :src="'/img/profilbilder' + employee.picture" :alt="employee.fullName">
                    <template v-slot:error>
                      <v-avatar color="primary" size="40">
                        <span class="white--text">{{ employee.fullName ? employee.fullName.slice(0, 1) : "?" }}</span>
                      </v-avatar>
                    </template>
                  </v-img>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">{{ employee.fullName }}</div>
                  <div class="text-caption grey--text">
                    <v-tooltip bottom v-if="employee.stats.consecutiveWeekends > 0">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="error" small v-bind="attrs" v-on="on" class="mr-1">mdi-alert-circle</v-icon>
                      </template>
                      <span>Arbeitet {{ employee.stats.consecutiveWeekends + 1 }} Wochenenden in Folge!</span>
                    </v-tooltip>
                    <span class="mr-2"
                      ><strong>{{ employee.stats.weekendCount }}</strong> WE</span
                    >
                    <span
                      ><strong>{{ employee.plannedHours.toFixed(1) }}h</strong> Geplant</span
                    >
                  </div>
                </div>
              </v-col>

              <v-col md="9" class="d-none d-md-flex">
                <v-row no-gutters>
                  <v-col
                    v-for="day in daysInMonth"
                    :key="`preview-${employee._id}-${day.format('D')}`"
                    class="text-center day-cell-preview"
                    :class="{
                      weekend: day.isoWeekday() >= 6,
                      'recurring-absence-day':
                        employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'recurringAbsence' ||
                        employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'freeDuringSchoolHoliday', // Optional: Highlight free school holiday days too
                    }">
                    <v-chip
                      v-if="employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'shift'"
                      :color="employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.data.shiftTemplate.color"
                      x-small
                      dark
                      class="shift-chip-preview">
                      {{ employee.dailyStatus?.[day.format("YYYY-MM-DD")]?.data.shiftTemplate?.shortName?.charAt(0) || "?" }}
                    </v-chip>

                    <v-chip
                      v-else-if="employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'schoolHoliday'"
                      color="orange darken-1"
                      x-small
                      dark
                      class="absence-chip-preview">
                      <v-icon x-small>mdi-beach</v-icon>
                    </v-chip>

                    <v-chip
                      v-else-if="employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'vacation'"
                      color="teal lighten-1"
                      x-small
                      dark
                      class="absence-chip-preview">
                      <v-icon x-small>mdi-palm-tree</v-icon>
                    </v-chip>

                    <v-chip
                      v-else-if="employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'school'"
                      color="blue lighten-1"
                      x-small
                      dark
                      class="absence-chip-preview">
                      <v-icon x-small>mdi-school</v-icon>
                    </v-chip>

                    <v-icon
                      v-else-if="employee.dailyStatus?.[day.format('YYYY-MM-DD')]?.type === 'freeDuringSchoolHoliday'"
                      x-small
                      color="orange lighten-2"
                      class="school-holiday-context-icon">
                      mdi-beach
                    </v-icon>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="employee-content">
            <v-row>
              <v-col cols="12" md="4">
                <v-card-title>Prognose & Statistik</v-card-title>
                <v-list dense flat color="transparent">
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-target</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ employee.sollStunden.toFixed(1) }} h</v-list-item-title>
                      <v-list-item-subtitle>Soll-Stunden ({{ employee.percentage }}%)</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-briefcase-clock</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ employee.plannedHours.toFixed(1) }} h</v-list-item-title>
                      <v-list-item-subtitle>Geplante Ist-Stunden</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item :class="employee.stats.prognosisColor">
                    <v-list-item-icon
                      ><v-icon :color="employee.stats.prognosisColor.replace('--text', '')">{{
                        employee.stats.prognosisIcon
                      }}</v-icon></v-list-item-icon
                    >
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ employee.stats.prognosisHours.toFixed(1) }} h</v-list-item-title>
                      <v-list-item-subtitle>Stunden-Differenz</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-plus-minus-variant</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ employee.overTime.toFixed(1) }} h</v-list-item-title>
                      <v-list-item-subtitle>Aktueller Saldo</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item :class="employee.stats.newSaldoColor">
                    <v-list-item-icon><v-icon :color="employee.stats.newSaldoColor.replace('--text', '')">mdi-calculator</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">{{ employee.stats.newSaldo.toFixed(1) }} h</v-list-item-title>
                      <v-list-item-subtitle>Prognose Neuer Saldo</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" md="8">
                <v-card-title>Geplante Schichten ({{ employee.stats.shiftCount }})</v-card-title>
                <div style="max-height: 300px; overflow-y: auto">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Datum</th>
                          <th>Schicht</th>
                          <th>Zeit</th>
                          <th>Dauer</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="shift in employee.shifts"
                          :key="shift._id"
                          :class="{ 'warning--text text--lighten-2': $moment(shift.date).isoWeekday() >= 6 }">
                          <td>{{ $moment(shift.date).format("dd, DD.MM.") }}</td>
                          <td>
                            <v-chip :color="shift.shiftTemplate.color" x-small dark class="mr-1"></v-chip>
                            {{ shift.shiftTemplate.name }}
                          </td>
                          <td>{{ shift.shiftTemplate.shortName }}</td>
                          <td>{{ shift.duration.toFixed(1) }} h</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "ShiftManagementRosterView",
  props: {
    planData: { type: Array, required: true },
    employees: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    startDate: { type: String, required: true },
    schoolHolidays: { type: Array, default: () => [] },
  },
  data() {
    return {
      // Basis-Stundensatz für 100%
      dailySollHours: 8.4,
    }
  },
  computed: {
    monthName() {
      return this.startDate ? this.$moment(this.startDate).format("MMMM YYYY") : ""
    },
    daysInMonth() {
      if (!this.startDate) return []
      const start = this.$moment(this.startDate).startOf("month")
      const end = this.$moment(this.startDate).endOf("month")
      const days = []
      let day = start.clone()
      while (day.isSameOrBefore(end, "day")) {
        days.push(day.clone())
        day.add(1, "day")
      }
      return days
    },
    // Die Haupt-Logik: Bereitet alle Mitarbeiterdaten für die Anzeige auf
    processedEmployees() {
      // Prüfe, ob die notwendigen Props vorhanden sind
      if (!this.employees || this.employees.length === 0 || !this.planData || !this.daysInMonth || this.daysInMonth.length === 0) {
        console.warn("processedEmployees skipped: Missing or empty props (employees, planData, daysInMonth).")
        return []
      }

      // Gib eine Warnung aus, wenn schoolHolidays fehlen, da dies für Lehrlinge wichtig ist.
      if (!this.schoolHolidays) {
        console.warn("processedEmployees: schoolHolidays prop is missing or undefined.")
      }

      return (
        this.employees
          .map((emp) => {
            // Stelle sicher, dass employee-Objekt die erwarteten Felder hat, sonst Fallback
            const employeeData = {
              _id: emp._id, // User ID
              fullName: emp.fullName || "Unbekannter Name",
              picture: emp.picture || "",
              overTime: emp.overTime || 0,
              sollStunden: emp.sollStunden || 0, // SollStunden sollten vom Parent kommen
              plannedHours: emp.plannedHours || 0, // plannedHours sollten vom Parent kommen
              // Berechne Prozentsatz als Fallback, falls nicht übergeben
              percentage:
                emp.percentage != null
                  ? emp.percentage
                  : emp.sollStunden && this.daysInMonth.filter((d) => d.isoWeekday() < 6).length > 0
                  ? (emp.sollStunden / (this.daysInMonth.filter((d) => d.isoWeekday() < 6).length * 8.4)) * 100
                  : 0,
              isApprentice: emp.isApprentice || false,
              schoolDays: emp.schoolDays || [],
              vacations: emp.vacations || [],
              planningSettings: emp.planningSettings || { recurringAbsence: [] }, // Fallback für planningSettings
            }

            // 1. Finde alle Schichten für diesen Mitarbeiter (aus planData Prop)
            const shifts = this.planData
              .filter((plan) => plan.assignedEmployee && plan.assignedEmployee._id === employeeData._id)
              .map((plan) => ({
                ...plan,
                // Berechne Dauer nur, wenn shiftTemplate vorhanden ist
                duration: plan.shiftTemplate ? this.calculateSingleShiftHours(plan.shiftTemplate) : 0,
              }))
              // Sortiere nach Datum, um korrekte Reihenfolge sicherzustellen
              .sort((a, b) => new Date(a.date) - new Date(b.date))

            // 2. Erstelle eine Map für den *täglichen Status* über den gesamten Monat
            const dailyStatus = {}
            this.daysInMonth.forEach((day) => {
              const dateStr = day.format("YYYY-MM-DD")
              const dayOfWeekISO = day.isoWeekday() // 1=Mo, 7=So
              let status = null // Standard: Frei

              // Prioritäten-Reihenfolge für Anzeige: Persönliche Ferien > Globale Feiertage > Schicht > Normaler Schultag > Wiederkehrende Abwesenheit

              // Check Persönliche Ferien (aus employeeData.vacations)
              const vacation = employeeData.vacations.find(
                (vac) =>
                  vac.dates &&
                  vac.dates.length > 0 && // Stelle sicher, dass Daten existieren
                  day.isBetween(vac.dates[0].date, vac.dates[vac.dates.length - 1].date, null, "[]")
              )
              if (vacation) {
                status = { type: "vacation" }
              }

              // TODO: Check globale Feiertage (z.B. aus einer externen Quelle oder Prop)
              // else if (isGlobalHoliday) { status = { type: 'globalHoliday' }; }

              // Dann zugewiesene Schicht prüfen (wenn keine Ferien)
              else {
                const shiftOnDay = shifts.find((s) => s.date === dateStr)
                if (shiftOnDay && shiftOnDay.shiftTemplate) {
                  // Stelle sicher, dass shiftTemplate existiert
                  status = { type: "shift", data: shiftOnDay }
                }
              }

              // Check für normalen Schultag (NUR wenn bisher frei UND KEINE Schulferien sind)
              const isSchoolHoliday = (this.schoolHolidays || []).some(
                (
                  sh // Sichere Prüfung falls schoolHolidays undefiniert
                ) => sh.startDate && sh.endDate && day.isBetween(sh.startDate, sh.endDate, null, "[]")
              )
              // Zeige "Schule" nur an, wenn der Status noch null ist (also keine Ferien/Schicht),
              // es ein Lehrling ist, es der Schultag ist UND KEINE Schulferien sind.
              if (!status && employeeData.isApprentice && !isSchoolHoliday && employeeData.schoolDays.some((sd) => sd.dayOfWeek === dayOfWeekISO)) {
                status = { type: "school" }
              }

              // Check für wiederkehrende Abwesenheit (nur wenn bisher frei)
              else if (!status && employeeData.planningSettings.recurringAbsence.includes(dayOfWeekISO)) {
                status = { type: "recurringAbsence" }
              }

              // Speichere den ermittelten Status für diesen Tag
              dailyStatus[dateStr] = status

              // *** Optional: Zusätzliche Info für Styling speichern, OB es ein Schulferientag ist ***
              // Diese Info kann genutzt werden, um z.B. den Hintergrund einer Zelle leicht zu ändern,
              // auch wenn eine Schicht angezeigt wird.
              if (isSchoolHoliday && employeeData.isApprentice) {
                if (dailyStatus[dateStr]) {
                  // Füge die Info zum bestehenden Status-Objekt hinzu
                  dailyStatus[dateStr].isSchoolHolidayContext = true
                } else {
                  // Wenn der Tag sonst frei wäre, markiere ihn speziell
                  dailyStatus[dateStr] = { type: "freeDuringSchoolHoliday", isSchoolHolidayContext: true } // Eigener Typ für freie Tage in Ferien
                }
              }
            }) // Ende daysInMonth.forEach

            // 3. Berechne Statistiken (unverändert zur vorherigen Version, verwendet 'shifts')
            const stats = this.calculateEmployeeStats(shifts, employeeData)
            console.log({
              ...employeeData, // Enthält _id, fullName, sollStunden, plannedHours etc. vom Parent
              shifts: shifts, // Die Liste der Schichten bleibt für die Detailansicht erhalten
              dailyStatus: dailyStatus, // Das Objekt mit dem Status für jeden Tag
              stats: stats, // Die berechneten Statistiken (Wochenenden, Saldo etc.)
            })
            // Gebe das aufbereitete Mitarbeiter-Objekt zurück
            return {
              ...employeeData, // Enthält _id, fullName, sollStunden, plannedHours etc. vom Parent
              shifts: shifts, // Die Liste der Schichten bleibt für die Detailansicht erhalten
              dailyStatus: dailyStatus, // Das Objekt mit dem Status für jeden Tag
              stats: stats, // Die berechneten Statistiken (Wochenenden, Saldo etc.)
            }
          })
          // Sortiere die Mitarbeiterliste alphabetisch nach Namen
          .sort((a, b) => a.fullName.localeCompare(b.fullName))
      )
    },
  },
  methods: {
    // Berechnet die Dauer einer einzelnen Schicht
    calculateSingleShiftHours(shiftTemplate) {
      if (!shiftTemplate || !shiftTemplate.times) return 0
      return shiftTemplate.times.reduce((sum, time) => {
        if (time.type === "Arbeitszeit" && time.start && time.end) {
          const start = this.$moment(time.start, "HH:mm")
          const end = this.$moment(time.end, "HH:mm")
          if (end.isBefore(start)) end.add(1, "day")
          return sum + end.diff(start, "hours", true)
        }
        return sum
      }, 0)
    },
    // Berechnet alle Prognosen und Warnungen für einen Mitarbeiter
    calculateEmployeeStats(shifts, employee) {
      let weekendCount = 0
      let consecutiveWeekends = 0
      let lastWeekendWorked = -2 // Startwert, der sicherstellt, dass die erste Woche kein Konsekutiv-Wochenende ist

      shifts.forEach((shift) => {
        const shiftDate = this.$moment(shift.date)
        const dayOfWeek = shiftDate.isoWeekday()
        const weekOfYear = shiftDate.isoWeek()

        if (dayOfWeek >= 6) {
          // Samstag oder Sonntag
          if (lastWeekendWorked !== weekOfYear) {
            weekendCount++
            if (lastWeekendWorked === weekOfYear - 1) {
              consecutiveWeekends++ // Diese Woche und letzte Woche gearbeitet
            }
            lastWeekendWorked = weekOfYear
          }
        }
      })

      // Prognosen berechnen
      const prognosisHours = (employee.plannedHours || 0) - (employee.sollStunden || 0)
      const newSaldo = (employee.overTime || 0) + prognosisHours

      // Farben und Icons für Prognosen
      const prognosisColor = prognosisHours < -2 ? "error--text" : prognosisHours > 2 ? "warning--text" : "success--text"
      const prognosisIcon = prognosisHours < -2 ? "mdi-arrow-down" : prognosisHours > 2 ? "mdi-arrow-up" : "mdi-check"
      const newSaldoColor = newSaldo < -5 ? "error--text" : newSaldo > 10 ? "warning--text" : "success--text" // Toleranter bei Gesamt-Saldo

      return {
        shiftCount: shifts.length,
        weekendCount: weekendCount,
        consecutiveWeekends: consecutiveWeekends, // Anzahl der *zusätzlichen* Wochenenden in Folge
        prognosisHours: prognosisHours,
        prognosisColor: prognosisColor,
        prognosisIcon: prognosisIcon,
        newSaldo: newSaldo,
        newSaldoColor: newSaldoColor,
      }
    },
  },
}
</script>

<style scoped>
/* Styling für Abwesenheits-Chips */
.absence-chip-preview {
  font-size: 0.65rem; /* Gleiche Grösse wie Schicht-Chip */
  height: 18px;
  width: 18px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Leichte Hervorhebung für wiederkehrende Abwesenheitstage */
.recurring-absence-day {
  background-color: rgba(255, 255, 255, 0.04) !important; /* Etwas dunklerer Hintergrund */
  /* Oder ein dezenter Rand: */
  /* border-left: 2px solid rgba(128, 128, 128, 0.3) !important; */
}

/* Optional: Sicherstellen, dass Wochenend-Highlight nicht überschrieben wird, falls gewünscht */
.weekend.recurring-absence-day {
  background-color: rgba(255, 255, 255, 0.06) !important; /* Kombination aus Weekend und Absence */
}
/* Header-Styling */
.roster-header {
  background-color: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  position: sticky;
  top: 0; /* Hält den Header oben, wenn man scrollt (falls Container scrollbar ist) */
  z-index: 3;
}
.day-cell-header {
  font-size: 0.8rem;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

/* Mitarbeiter-Panel Styling */
.employee-panel {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.employee-header {
  transition: background-color 0.2s ease-in-out;
}
.employee-header:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

/* Mini-Kalender-Vorschau */
.day-cell-preview {
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  height: 48px; /* Höhe anpassen, um in den Header zu passen */
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  padding: 0 2px;
}
.shift-chip-preview {
  font-size: 0.65rem;
  height: 18px;
  width: 18px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Hervorhebung für Wochenende */
.weekend {
  background-color: rgba(255, 255, 255, 0.03);
}

/* Inhalt des aufgeklappten Panels */
.employee-content {
  background-color: rgba(0, 0, 0, 0.1);
}

/* Animation für die Karte (optional, aber "schick") */
.employee-card.ready-card {
  animation: pulse-green 1.5s infinite;
}
@keyframes pulse-green {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-success-base-rgb), 0.4);
    border-color: rgba(var(--v-success-base-rgb), 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--v-success-base-rgb), 0);
    border-color: rgba(var(--v-success-base-rgb), 0.8);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-success-base-rgb), 0);
    border-color: rgba(var(--v-success-base-rgb), 0.5);
  }
}
</style>
